import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import {
  getPseaAssessmentForContractor,
  getAgencyPseaAssessmentForContractor,
} from "common/services/psea-assessments.service";

/**
 * Resolves how the current user reads a contractor's PSEA assessment. Mirrors
 * `usePseaCreator` / `usePseaUpdater`: the main API is used when permitted,
 * otherwise the agency `findOne` route. The main API takes precedence when
 * both are present.
 *
 * Both paths are unscoped by design — every agency can see a contractor's PSEA
 * assessment regardless of which agency carried it out. (The agency `find`
 * endpoint, used by the assessments list page, stays scoped to the caller's
 * own agency.)
 */
export const usePseaReader = () => {
  const { userProfile } = useProfile();
  const permissions = userProfile?.permissions;

  const canReadMain = isAllowed(permissions, [
    { resource: "psea-assessment", action: "find" },
  ]);
  const canReadAgency = isAllowed(permissions, [
    { resource: "psea-assessment", action: "findOne", controller: "agency" },
  ]);

  const fetchByContractor = canReadMain
    ? getPseaAssessmentForContractor
    : canReadAgency
    ? getAgencyPseaAssessmentForContractor
    : null;

  return { canRead: canReadMain || canReadAgency, fetchByContractor };
};
